/**
 * ClientRecorder Component
 * Records local participant video + audio in 2-second chunks for backup/redundancy
 * Chunks are stored in IndexedDB and continuously uploaded to S3
 */

import { useEffect, useRef, useState } from 'react';
import { useLocalParticipant, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import axios from 'axios';
import { saveChunk } from '../services/indexedDB';
import uploadManager from '../services/uploadManager';

const ClientRecorder = ({ meetingId, agencyId, participantIdentity, isRecording, isPaused }) => {
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone], { onlySubscribed: false });

  const mediaRecorderRef = useRef(null);
  const segmentNumberRef = useRef(1);
  const chunkIndexRef = useRef(0);
  const isRecordingRef = useRef(false);
  const startAttemptCountRef = useRef(0);
  const wasPausedRef = useRef(false);
  const startInProgressRef = useRef(false);
  const tracksRef = useRef(tracks);
  // Whether the segment currently being recorded has a video track, so a
  // camera toggled on/off mid-meeting can be detected and trigger a rollover
  const hasVideoRef = useRef(false);
  const pendingRolloverRef = useRef(false);

  // A rejoin after a full page reload (tab closed, not just a LiveKit
  // reconnect) loses segmentNumberRef's in-memory state entirely, so it
  // would otherwise restart at segment 1 / chunk 0 - the exact same S3 keys
  // the pre-reload session already uploaded to - and silently overwrite that
  // earlier recording instead of appending after it. Block the first start
  // until the server has told us which segment to resume from.
  const [resumePointReady, setResumePointReady] = useState(false);

  // Retries read tracks through this ref so a start that is waiting on a
  // track to publish picks up the new one instead of its stale closure
  tracksRef.current = tracks;

  // Fetch where this participant's local recording already left off, once
  // per mount, before any recording is allowed to start.
  useEffect(() => {
    let cancelled = false;

    const fetchResumePoint = async () => {
      try {
        const res = await axios.get(
          `/api/livekit/meetings/${meetingId}/local-recording/resume-point`,
          {
            params: { participant_identity: participantIdentity, agency_id: agencyId },
            withCredentials: true
          }
        );
        if (!cancelled && res.data?.next_segment) {
          segmentNumberRef.current = res.data.next_segment;
        }
      } catch (error) {
        console.error('[ClientRecorder] Failed to fetch local recording resume point, defaulting to segment 1:', error);
      } finally {
        if (!cancelled) {
          setResumePointReady(true);
        }
      }
    };

    if (meetingId && participantIdentity) {
      fetchResumePoint();
    } else {
      setResumePointReady(true);
    }

    return () => {
      cancelled = true;
    };
  }, [meetingId, participantIdentity, agencyId]);

  // Handle recording state changes
  useEffect(() => {
    console.log('[ClientRecorder] Effect triggered:', {
      isRecording,
      isPaused,
      isRecordingRef: isRecordingRef.current,
      localParticipant: !!localParticipant,
      tracksCount: tracks.length
    });

    // Start recording if isRecording and not paused.
    // useTracks hands back a new array on every render, so this effect re-runs
    // constantly - startInProgressRef keeps a start that is still waiting on
    // its tracks from being kicked off a second time, which would leave two
    // recorders writing to the same chunk keys.
    if (isRecording && !isPaused && !isRecordingRef.current && !startInProgressRef.current && resumePointReady && participantIdentity && localParticipant && tracks.length > 0) {
      // Check if we're resuming from a pause
      if (wasPausedRef.current) {
        console.log('[ClientRecorder] Resuming client-side recording (new segment)');
        segmentNumberRef.current += 1;
        chunkIndexRef.current = 0;
        wasPausedRef.current = false;
      } else {
        console.log('[ClientRecorder] Starting client-side recording');
      }
      startClientRecording();
    }
    // Stop recording if paused
    else if (isPaused && isRecordingRef.current) {
      console.log('[ClientRecorder] Pausing client-side recording (segment complete)');
      wasPausedRef.current = true;
      stopClientRecording();
    }
    // Stop recording completely
    else if (!isRecording && isRecordingRef.current) {
      console.log('[ClientRecorder] Stopping client-side recording');
      stopClientRecording();
      // Keep advancing the segment rather than resetting to 1, so recording
      // again in the same session cannot overwrite the earlier chunk keys
      segmentNumberRef.current += 1;
      chunkIndexRef.current = 0;
      wasPausedRef.current = false;
    }
    // Camera turned on or off mid-recording: the active MediaRecorder was
    // built from a fixed MediaStream snapshot taken when it started, so a
    // track that starts/stops publishing afterward is never picked up on its
    // own. Roll over to a new segment so the new track state gets recorded.
    else if (isRecording && !isPaused && isRecordingRef.current && !startInProgressRef.current) {
      const currentHasVideo = tracks.some(
        (t) => t.source === Track.Source.Camera && t.publication?.track?.mediaStreamTrack
      );
      if (currentHasVideo !== hasVideoRef.current) {
        console.log(
          `[ClientRecorder] Camera turned ${currentHasVideo ? 'on' : 'off'} mid-recording - rolling to a new segment`
        );
        rolloverSegment();
      }
    }
  }, [isRecording, isPaused, resumePointReady, participantIdentity, localParticipant, tracks]);

  // Ends the current segment and immediately starts a new one that picks up
  // the current track state, instead of the gap-free pause/resume flow a
  // user triggers manually (see onstop below, which fires the restart here).
  const rolloverSegment = () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return;
    }
    pendingRolloverRef.current = true;
    mediaRecorderRef.current.stop();
  };

  const startClientRecording = async () => {
    startInProgressRef.current = true;

    try {
      // Find audio and video tracks
      const currentTracks = tracksRef.current;
      const audioTrack = currentTracks.find(t => t.source === Track.Source.Microphone);
      const videoTrack = currentTracks.find(t => t.source === Track.Source.Camera);

      // Wait for tracks to be fully initialized
      // Note: useTracks returns objects with structure { publication, source, participant }
      // The actual track is at track.publication.track.mediaStreamTrack
      if (!audioTrack?.publication?.track?.mediaStreamTrack && !videoTrack?.publication?.track?.mediaStreamTrack) {
        startAttemptCountRef.current += 1;
        console.warn(`[ClientRecorder] Tracks not yet initialized, attempt ${startAttemptCountRef.current}, retrying in 500ms...`);

        // Retry up to 10 times (5 seconds total)
        if (startAttemptCountRef.current < 10) {
          setTimeout(() => {
            startClientRecording();
          }, 500);
        } else {
          console.error('[ClientRecorder] Failed to initialize tracks after 10 attempts');
          startAttemptCountRef.current = 0;
          startInProgressRef.current = false;
        }
        return;
      }

      // Reset attempt counter on success
      startAttemptCountRef.current = 0;

      // Collect available media stream tracks
      const mediaStreamTracks = [];

      if (audioTrack?.publication?.track?.mediaStreamTrack) {
        mediaStreamTracks.push(audioTrack.publication.track.mediaStreamTrack);
        console.log('[ClientRecorder] Audio track found');
      }

      if (videoTrack?.publication?.track?.mediaStreamTrack) {
        mediaStreamTracks.push(videoTrack.publication.track.mediaStreamTrack);
        console.log('[ClientRecorder] Video track found');
      }

      if (mediaStreamTracks.length === 0) {
        console.warn('[ClientRecorder] No media stream tracks available yet');
        startInProgressRef.current = false;
        return;
      }

      const mediaStream = new MediaStream(mediaStreamTracks);
      const hasVideo = videoTrack?.publication?.track?.mediaStreamTrack != null;
      hasVideoRef.current = hasVideo;

      // Choose codec based on browser support and available tracks
      let mimeType;
      if (hasVideo) {
        // Try video codecs with audio
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
          mimeType = 'video/webm;codecs=vp9,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
          mimeType = 'video/webm;codecs=vp8,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
          mimeType = 'video/webm';
        } else {
          mimeType = 'video/mp4';
        }
      } else {
        // Audio only fallback
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else {
          mimeType = 'audio/ogg';
        }
      }

      console.log('[ClientRecorder] Using MIME type:', mimeType, 'hasVideo:', hasVideo);

      const recorderOptions = {
        mimeType: mimeType,
        audioBitsPerSecond: 64000  // 64kbps for audio
      };

      // Add video bitrate if recording video
      if (hasVideo) {
        recorderOptions.videoBitsPerSecond = 1500000;  // 1.5 Mbps for video
      }

      const recorder = new MediaRecorder(mediaStream, recorderOptions);

      recorder.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0) {
          // Claim the index before awaiting anything - the handler is async and
          // fires every 2 seconds, so reading it after the save lets two chunks
          // claim the same index and overwrite each other in S3
          const segmentNumber = segmentNumberRef.current;
          const chunkIndex = chunkIndexRef.current;
          chunkIndexRef.current += 1;

          console.log(`[ClientRecorder] Chunk available: segment=${segmentNumber}, chunk=${chunkIndex}, size=${event.data.size} bytes`);

          const chunkData = {
            meeting_id: meetingId,
            agency_id: agencyId,
            participant_identity: participantIdentity,
            segment_number: segmentNumber,
            chunk_index: chunkIndex,
            blob: event.data,
            size_bytes: event.data.size,
            timestamp: Date.now(),
            upload_status: 'pending',
            retry_count: 0,
            last_retry_at: null,
            has_video: hasVideo
          };

          try {
            // Save to IndexedDB
            const chunkId = await saveChunk(chunkData);
            console.log(`[ClientRecorder] Chunk saved to IndexedDB: id=${chunkId}`);

            // Enqueue for upload
            uploadManager.enqueueChunk({ ...chunkData, id: chunkId });
          } catch (error) {
            console.error('[ClientRecorder] Error saving chunk:', error);

            // Check if quota exceeded
            if (error.name === 'QuotaExceededError') {
              console.error('[ClientRecorder] IndexedDB quota exceeded! Stopping recording.');
              stopClientRecording();
              // TODO: Notify user about quota issue
            }
          }
        }
      };

      recorder.onerror = (event) => {
        console.error('[ClientRecorder] MediaRecorder error:', event.error);
      };

      recorder.onstart = () => {
        console.log(`[ClientRecorder] MediaRecorder started: segment=${segmentNumberRef.current}`);
      };

      recorder.onstop = () => {
        console.log(`[ClientRecorder] MediaRecorder stopped: segment=${segmentNumberRef.current}`);
        isRecordingRef.current = false;

        if (pendingRolloverRef.current) {
          pendingRolloverRef.current = false;
          segmentNumberRef.current += 1;
          chunkIndexRef.current = 0;
          startClientRecording();
        }
      };

      // Start recording with 2-second timeslice (generates chunks every 2 seconds)
      // This provides redundancy - if browser crashes, you only lose max 2 seconds
      // The flag is set before start() because the start event is dispatched
      // asynchronously, and until then this effect would start a second recorder
      isRecordingRef.current = true;
      mediaRecorderRef.current = recorder;
      recorder.start(2000);
      startInProgressRef.current = false;

    } catch (error) {
      console.error('[ClientRecorder] Error starting recording:', error);
      isRecordingRef.current = false;
      startInProgressRef.current = false;
    }
  };

  const stopClientRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();

        // Wait a bit for the final chunk to be processed
        setTimeout(() => {
          mediaRecorderRef.current = null;
        }, 100);
      } catch (error) {
        console.error('[ClientRecorder] Error stopping recording:', error);
        mediaRecorderRef.current = null;
        isRecordingRef.current = false;
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // This component doesn't render anything - it's purely functional
  return null;
};

export default ClientRecorder;
